import { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';

import {
  PLATFORM_LABELS,
  SUPPORT_BODY_MAX,
  SUPPORT_TICKET_CATEGORY_LABELS,
  SUPPORT_TICKET_STATUS_LABELS,
  supportTicketIsLive,
  type Platform,
  type SupportRefSummary,
  type SupportTicketDetailResponse,
} from '@nexus/shared';

import { ApiError, apiGet, apiSend } from '../lib/api';
import { CheckList } from '../components/CheckList';
import {
  Alert,
  Badge,
  Button,
  Card,
  ConfirmButton,
  ExternalLink,
  Fact,
  Facts,
  LoadErrorPage,
  LoadingPage,
  Money,
  PageHeader,
  TextField,
  When,
  statusLabel,
  statusTone,
} from '../ui';

/**
 * One ticket, from the creator's side: the thread, the reply box, and the
 * thing the ticket is about beside it.
 *
 * Opening the page is reading it — the API stamps the operator's messages
 * read on this GET — so the nav badge clears the moment the thread is on
 * screen, not when a button is pressed.
 */
export function SupportTicket(): React.JSX.Element {
  const { id } = useParams();
  const [data, setData] = useState<SupportTicketDetailResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [reply, setReply] = useState('');
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setError(null);
    try {
      setData(await apiGet<SupportTicketDetailResponse>(`/support/tickets/${id}`));
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not load the ticket.');
    }
  }, [id]);

  useEffect(() => {
    void load();
  }, [load]);

  async function send(event: React.FormEvent): Promise<void> {
    event.preventDefault();
    if (reply.trim().length === 0) return;
    setBusy(true);
    setActionError(null);
    try {
      setData(await apiSend<SupportTicketDetailResponse>('POST', `/support/tickets/${id}/messages`, { body: reply.trim() }));
      setReply('');
    } catch (err) {
      setActionError(err instanceof ApiError ? err.message : 'Could not send that.');
    } finally {
      setBusy(false);
    }
  }

  async function close(): Promise<void> {
    setBusy(true);
    setActionError(null);
    try {
      setData(await apiSend<SupportTicketDetailResponse>('POST', `/support/tickets/${id}/close`, {}));
    } catch (err) {
      setActionError(err instanceof ApiError ? err.message : 'Could not close it.');
    } finally {
      setBusy(false);
    }
  }

  if (error && data === null) {
    return (
      <LoadErrorPage eyebrow="Support" title="Ticket" heading="Could not load the ticket." onRetry={() => void load()}>
        <span>{error}</span>
      </LoadErrorPage>
    );
  }
  if (data === null) return <LoadingPage eyebrow="Support" title="Ticket" />;

  const { ticket, messages, ref } = data;
  const live = supportTicketIsLive(ticket.status);

  return (
    <>
      <PageHeader
        eyebrow={
          <>
            Support <span aria-hidden="true">·</span> {SUPPORT_TICKET_CATEGORY_LABELS[ticket.category]}
          </>
        }
        title={ticket.subject}
        lead={
          ticket.status === 'WAITING_ON_CREATOR'
            ? 'Nexus replied and is waiting for your answer.'
            : ticket.status === 'OPEN'
              ? 'A person will reply here. You are told by email when they do.'
              : ticket.status === 'RESOLVED'
                ? 'Resolved. Reply if it is not, and it reopens.'
                : 'Closed. Open a new ticket if there is more.'
        }
        actions={
          <>
            <Badge tone={statusTone(ticket.status, 'support')} testId="ticket-status">
              {SUPPORT_TICKET_STATUS_LABELS[ticket.status]}
            </Badge>
            <Link className="btn btn-ghost small" to="/support">
              All tickets
            </Link>
          </>
        }
      />

      {actionError && <Alert tone="critical">{actionError}</Alert>}

      <div className={ref ? 'split reference' : undefined}>
        <div className="stack">
          <Card title="The conversation" label="The conversation">
            <ol className="thread">
              {messages.map((m) => (
                <li key={m.id} data-role={m.authorRole}>
                  <div className="msg-head">
                    <span className="msg-author">{m.authorName}</span>
                    <When value={m.createdAt} withTime />
                  </div>
                  <p className="msg-body">{m.body}</p>
                </li>
              ))}
            </ol>
          </Card>

          <Card title={live || ticket.status === 'RESOLVED' ? 'Reply' : 'This ticket is closed'}>
            {ticket.status === 'CLOSED' ? (
              <p className="muted">
                Closed tickets stay as they are.{' '}
                <Link to="/support/new">Open a new one</Link> and it reaches the same person.
              </p>
            ) : (
              <form onSubmit={send} className="form">
                <TextField
                  label="Your message"
                  name="reply"
                  textarea
                  rows={4}
                  value={reply}
                  onChange={(e) => setReply(e.target.value)}
                  maxLength={SUPPORT_BODY_MAX}
                  required
                />
                <div className="actions">
                  <Button type="submit" variant="primary" disabled={busy || reply.trim().length === 0}>
                    {busy ? 'Sending…' : 'Send'}
                  </Button>
                  {live && (
                    <ConfirmButton
                      label="Close this ticket"
                      variant="ghost"
                      title="Close this ticket?"
                      question="You will not be able to reply on it afterwards; a new ticket reaches the same person."
                      confirmLabel="Close it"
                      disabled={busy}
                      onConfirm={() => void close()}
                    />
                  )}
                </div>
              </form>
            )}
          </Card>
        </div>

        {ref && <RefCard ref_={ref} />}
      </div>
    </>
  );
}

/** What the ticket is about, as the creator's own screens show it. */
export function RefCard({ ref_ }: { ref_: SupportRefSummary }): React.JSX.Element {
  switch (ref_.type) {
    case 'PostSubmission':
      return (
        <Card
          title="The submission"
          eyebrow={
            <>
              {ref_.campaignName} <span aria-hidden="true">·</span> {PLATFORM_LABELS[ref_.platform as Platform]}
            </>
          }
          aside={<Badge tone={statusTone(ref_.status, 'submission')}>{ref_.statusLabel}</Badge>}
        >
          <p>
            <ExternalLink href={ref_.postUrl} />
          </p>
          {ref_.rejectionReason && <Alert tone="critical">{ref_.rejectionReason}</Alert>}
          {ref_.holdReason && <Alert tone="waiting">{ref_.holdReason}</Alert>}
          {ref_.supersededBy && (
            <Alert tone="positive">The checks were re-run and this submission was replaced by a fresh attempt.</Alert>
          )}
          <Facts>
            <Fact label="Submitted">
              <When value={ref_.submittedAt} withTime />
            </Fact>
            <Fact label="Decided by">
              {ref_.rejectedBy === 'operator' ? 'A person' : ref_.rejectedBy === 'automatic' ? 'The automatic checks' : '—'}
            </Fact>
          </Facts>
          <details className="reveal">
            <summary>The checks</summary>
            <CheckList checks={ref_.checks} stoppedAt={ref_.stoppedAt} />
          </details>
        </Card>
      );
    case 'Payout':
      return (
        <Card
          title="The payout"
          aside={
            <Badge tone={statusTone(ref_.requested ? 'REQUESTED' : ref_.status, 'payout')}>
              {ref_.requested ? 'Requested' : statusLabel(ref_.status, 'payout')}
            </Badge>
          }
        >
          <Facts>
            <Fact label="Amount">
              <Money cents={ref_.amountCents} />
            </Fact>
            <Fact label="Requested">
              <When value={ref_.requestedAt} />
            </Fact>
            <Fact label="Paid">
              <When value={ref_.paidAt} />
            </Fact>
            <Fact label="Sent to">{ref_.methodLabel ?? '—'}</Fact>
            <Fact label="Reference">
              {ref_.paymentReference ? <code className="chip quiet">{ref_.paymentReference}</code> : '—'}
            </Fact>
          </Facts>
          {ref_.failureReason && <Alert tone="critical">{ref_.failureReason}</Alert>}
          {ref_.attempts.length > 0 && (
            <ul className="rows">
              {ref_.attempts.map((a) => (
                <li key={a.attemptNumber}>
                  <span className="muted">
                    Attempt {a.attemptNumber}: {a.status.toLowerCase()} on <When value={a.submittedAt} />
                  </span>
                </li>
              ))}
            </ul>
          )}
        </Card>
      );
    case 'ReferralBonus':
      return (
        <Card title="The referral bonus" aside={<Badge tone={statusTone(ref_.status)}>{statusLabel(ref_.status)}</Badge>}>
          <Facts>
            <Fact label="Amount">
              <Money cents={ref_.amountCents} />
            </Fact>
            <Fact label="From">{ref_.refereeName}</Fact>
            <Fact label="Accrued">
              <When value={ref_.createdAt} />
            </Fact>
          </Facts>
          {ref_.reversalReason && <Alert tone="critical">{ref_.reversalReason}</Alert>}
        </Card>
      );
    case 'SocialAccount':
      return (
        <Card
          title="The connected account"
          aside={
            <Badge tone={statusTone(ref_.verificationStatus, 'connection')}>
              {statusLabel(ref_.verificationStatus, 'connection')}
            </Badge>
          }
        >
          <Facts>
            <Fact label="Account">
              {PLATFORM_LABELS[ref_.platform as Platform]} {ref_.handle}
            </Fact>
            <Fact label="Connected">
              <When value={ref_.verifiedAt} />
            </Fact>
          </Facts>
          {ref_.revokedReason && <Alert tone="critical">{ref_.revokedReason}</Alert>}
        </Card>
      );
  }
}
